import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Key, Plus, Copy, Trash2, Power, PowerOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const APIKeys = () => {
  const { token } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      const response = await axios.get(`${API}/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(response.data);
    } catch (error) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await axios.post(
        `${API}/api-keys?name=${encodeURIComponent(keyName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewKey(response.data.key);
      toast.success('API key created! Copy it now - it won\'t be shown again.');
      setKeyName('');
      fetchAPIKeys();
    } catch (error) {
      toast.error('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard!');
  };

  const handleToggleKey = async (keyId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/api-keys/${keyId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`API key ${currentStatus ? 'disabled' : 'enabled'}`);
      fetchAPIKeys();
    } catch (error) {
      toast.error('Failed to toggle API key');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure? This cannot be undone.')) return;

    try {
      await axios.delete(`${API}/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('API key deleted');
      fetchAPIKeys();
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SkipLink />
        <Header />
        <main id="main-content" className="flex items-center justify-center min-h-screen">
          <div className="text-center" role="status">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d4ed8] mx-auto"></div>
            <p className="mt-4 text-[#475569]">Loading API keys...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SkipLink />
      <Header />
      
      <main id="main-content" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                API Keys
              </h1>
              <p className="text-[#475569]">Manage API keys for programmatic access</p>
            </div>
            
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]">
                  <Plus className="h-4 w-4 mr-2" />
                  Create API Key
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create API Key</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateKey} className="space-y-4">
                  <div>
                    <Label htmlFor="key-name">Key Name</Label>
                    <Input
                      id="key-name"
                      value={keyName}
                      onChange={(e) => setKeyName(e.target.value)}
                      required
                      placeholder="Production API Key"
                      className="mt-1"
                    />
                  </div>
                  {newKey && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm font-medium text-yellow-800 mb-2">
                        ⚠️ Copy this key now - it won't be shown again!
                      </p>
                      <div className="flex items-center gap-2">
                        <Input
                          value={newKey}
                          readOnly
                          className="font-mono text-sm"
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleCopyKey(newKey)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <Button type="submit" disabled={creating}>
                      {creating ? 'Creating...' : 'Create'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowCreateDialog(false);
                        setNewKey(null);
                      }}
                    >
                      {newKey ? 'Close' : 'Cancel'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {apiKeys.length === 0 ? (
            <Card className="p-12 text-center border border-[#E2E8F0]">
              <Key className="h-12 w-12 text-[#475569] mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                No API Keys Yet
              </h2>
              <p className="text-[#475569] mb-6">
                Create an API key to access Evenly Respond programmatically
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {apiKeys.map((key, index) => (
                <motion.div
                  key={key.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="p-6 border border-[#E2E8F0]">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {key.name}
                          </h3>
                          {key.is_active ? (
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                              Active
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                              Disabled
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#475569] font-mono mb-2">
                          {key.key_masked}
                        </p>
                        <div className="text-xs text-[#475569]">
                          <p>Created: {new Date(key.created_at).toLocaleDateString()}</p>
                          {key.last_used && (
                            <p>Last used: {new Date(key.last_used).toLocaleString()}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleKey(key.id, key.is_active)}
                          className="rounded-full"
                        >
                          {key.is_active ? (
                            <PowerOff className="h-4 w-4" />
                          ) : (
                            <Power className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteKey(key.id)}
                          className="rounded-full text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          {/* Documentation */}
          <Card className="p-6 border border-[#E2E8F0] mt-8">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              API Documentation
            </h2>
            <div className="space-y-4 text-sm">
              <div>
                <p className="font-medium mb-2">Authentication</p>
                <code className="block p-3 bg-gray-50 rounded">
                  Authorization: Bearer YOUR_API_KEY
                </code>
              </div>
              <div>
                <p className="font-medium mb-2">Base URL</p>
                <code className="block p-3 bg-gray-50 rounded">
                  {BACKEND_URL}/api
                </code>
              </div>
              <div>
                <p className="font-medium mb-2">Example: Get QR Codes</p>
                <code className="block p-3 bg-gray-50 rounded">
                  curl -H "Authorization: Bearer YOUR_API_KEY" {BACKEND_URL}/api/qr-codes
                </code>
              </div>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};
